export interface FetchParams {
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
  body?: object;
  headers?: [string, string][] | Record<string, string> | Headers;
  // Set 'manual' when calling an endpoint that returns a cross-origin 3xx the FE wants to
  // intercept itself (e.g. /auth/verify-otp's 301 to the admin origin).
  redirect?: RequestRedirect;
  // External-share token: routes the call to that share's public mirror. Opt-in per
  // call — nothing is inferred from the path.
  shareToken?: string;
  signal?: AbortSignal;
}

import { AuthFailureCode } from './auth-failure-code';
import { ClientInfoCookieName } from './cookie-names';
// Land on /auth/signin (not /2fa-setup) — session is gone in both cases. Typed exhaustively
// so adding a new AuthFailureCode requires an explicit redirect-or-null decision here.
const SIGNIN_REDIRECT_BY_CODE: Record<AuthFailureCode, string | null> = {
  [AuthFailureCode.SESSION_INVALIDATED]:
    '/auth/signin?reason=session_invalidated',
  [AuthFailureCode.SESSION_EXPIRED]: '/auth/signin?reason=session_expired',
  [AuthFailureCode.OTP_REJECTED]: null,
  [AuthFailureCode.RATE_LIMITED]: null,
  [AuthFailureCode.ENROLLMENT_CONFLICT]: null,
};

let sessionTerminationHandled = false;

if (typeof window !== 'undefined') {
  // pagehide (not unload) so the flag resets across bfcache restores too — without
  // this, a fresh 401 after re-auth in the same JS context gets silently swallowed.
  window.addEventListener('pagehide', () => {
    sessionTerminationHandled = false;
  });
}

async function handleSessionTerminatedResponse(
  response: Response
): Promise<void> {
  if (response.status !== 401) return;
  if (typeof window === 'undefined') return;
  if (sessionTerminationHandled) return;

  let body: { code?: string } | null = null;
  try {
    body = await response.clone().json();
  } catch {
    return;
  }
  const code = body?.code as AuthFailureCode | undefined;
  const redirect = code ? SIGNIN_REDIRECT_BY_CODE[code] : null;
  if (!redirect) return;

  sessionTerminationHandled = true;
  // Local wipe — covers older path-scoped cookies the server's clear can't match.
  document.cookie = `${ClientInfoCookieName}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
  // replace, not assign — back button shouldn't return to the broken state.
  window.location.replace(redirect);
}

export class FetchError extends Error {
  info: { [key: string]: unknown };

  status: number;

  constructor(
    message: string,
    info: { [key: string]: unknown },
    status: number
  ) {
    super(message);
    this.info = info;
    this.status = status;
  }
}

export const fetcher = async (
  requestedUrl: string,
  data: FetchParams,
  token?: string,
  clerkToken?: string,
  fetchHeaders?: HeadersInit
) => {
  const url = data.shareToken
    ? `public/share/${encodeURIComponent(data.shareToken)}/${requestedUrl}`
    : requestedUrl;
  const headers = fetchHeaders || { 'Content-Type': 'application/json' };
  if (!token && !clerkToken) {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_SERVER_URL}/api/v1/${url}`,
      {
        method: data.method,
        body: JSON.stringify(data.body),
        credentials: 'include',
        headers,
        ...(data.redirect && { redirect: data.redirect }),
        ...(data.signal && { signal: data.signal }),
      }
    );

    if (!data.shareToken) await handleSessionTerminatedResponse(response);
    return response;
  }

  if (clerkToken) {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_SERVER_URL}/api/v1/${url}`,
      {
        method: data.method,
        body: JSON.stringify(data.body),
        headers: {
          ...headers,
          Authorization: `Bearer ${clerkToken}`,
        },
        ...(data.redirect && { redirect: data.redirect }),
        ...(data.signal && { signal: data.signal }),
      }
    );

    await handleSessionTerminatedResponse(response);
    return response;
  }

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_SERVER_URL}/api/v1/${url}`,
    {
      method: data.method,
      body: JSON.stringify(data.body),
      headers: {
        ...headers,
        Authorization: `Commenda ${token}`,
      },
      ...(data.redirect && { redirect: data.redirect }),
      ...(data.signal && { signal: data.signal }),
    }
  );

  await handleSessionTerminatedResponse(response);
  return response;
};

export interface MultipartFetchParams {
  method: 'POST' | 'PUT' | 'PATCH';
  body: FormData;
}

// Sibling of `fetcher` for endpoints that take the file bytes themselves rather than an S3
// reference. `fetcher` cannot be reused: it JSON.stringify's the body in all three branches,
// which turns a FormData into the string "{}".
export const multipartFetcher = async (
  url: string,
  data: MultipartFetchParams
): Promise<Response> => {
  // No headers at all, deliberately: omitting Content-Type is what makes the browser emit
  // `multipart/form-data; boundary=...`. Setting it by hand produces a body the server cannot parse.
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_SERVER_URL}/api/v1/${url}`,
    {
      method: data.method,
      body: data.body,
      credentials: 'include',
    }
  );

  await handleSessionTerminatedResponse(response);
  return response;
};

export const SWRFetcher = (
  data: FetchParams,
  token?: string,
  clerkToken?: string
) => {
  let res;
  return async (url: string) => {
    if (data?.headers) {
      res = await fetcher(url, data, token, clerkToken, data?.headers);
    } else {
      res = await fetcher(url, data, token, clerkToken);
    }

    const responseData = await res.json();

    if (!res.ok) {
      throw new FetchError(
        'An error occurred while fetching the data.',
        responseData,
        res.status
      );
    }

    return responseData;
  };
};
